import { createAsyncThunk } from '@reduxjs/toolkit';
import { invoicesApi } from './api';

export const fetchUserInvoices = createAsyncThunk(
  'invoices/fetchUserInvoices',
  async ({ page = 1, limit = 0, searchTerm = '', statusFilters = [] }, { rejectWithValue }) => {
    try {
      const statusParams = statusFilters.length > 0 
        ? statusFilters.map(status => `status=${status}`).join('&')
        : '';
      
      const baseUrl = `/invoices?page=${page}&limit=${limit}&search=${searchTerm}`;
      const url = statusParams ? `${baseUrl}&${statusParams}` : baseUrl;
      
      const response = await invoicesApi.getUserInvoices(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch invoices');
    }
  }
);

export const createInvoice = createAsyncThunk(
  'invoices/createInvoice',
  async (invoiceData, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.createInvoice(invoiceData);
      await dispatch(fetchUserInvoices({ page: 1, limit: 0 }));
      await dispatch(fetchUserDraftInvoices());
      return response.data?.invoice;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create invoice');
    }
  }
);

export const fetchInvoiceDetails = createAsyncThunk(
  'invoices/fetchInvoiceDetails',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await invoicesApi.getInvoiceDetails(invoiceId);
      return response.data.invoice;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch invoice details');
    }
  }
);

export const createAdminInvoice = createAsyncThunk(
  'invoices/createAdminInvoice',
  async (invoiceData, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.createAdminInvoice(invoiceData);
      await dispatch(fetchAllInvoices({ page: 1, limit: 0 }));
      await dispatch(fetchAdminDraftInvoices({ page: 1, limit: 0 }));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create invoice'
      );
    }
  }
);

export const fetchAllInvoices = createAsyncThunk(
  'invoices/fetchAllInvoices',
  async ({ page = 1, limit = 10, searchTerm = '', statusFilters = [] }, { rejectWithValue }) => {
    try {
      const statusParams = statusFilters.length > 0 
        ? statusFilters.map(status => `status=${status}`).join('&')
        : '';
      
      const baseUrl = `/admin/all-invoices?page=${page}&limit=${limit}&search=${searchTerm}`;
      const url = statusParams ? `${baseUrl}&${statusParams}` : baseUrl;
      
      const response = await invoicesApi.getAllInvoices(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch invoices');
    }
  }
);

export const fetchAdminInvoiceDetails = createAsyncThunk(
  'invoices/fetchAdminInvoiceDetails',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await invoicesApi.getAdminInvoiceDetails(invoiceId);
      return response.data.invoice;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch invoice details');
    }
  }
);

export const updateInvoiceStatus = createAsyncThunk(
  'invoices/updateStatus',
  async ({ invoiceId, status, reason }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.updateInvoiceStatus(invoiceId, { status, reason });
      await dispatch(fetchAdminInvoiceDetails(invoiceId));
      await dispatch(fetchAllInvoices({ page: 1, limit: 0 }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update invoice status');
    }
  }
);

export const updateInvoiceTerms = createAsyncThunk(
  'invoices/updateTerms',
  async ({ invoiceId, termId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.updateInvoiceTerms(invoiceId, { termId });
      await dispatch(fetchAdminInvoiceDetails(invoiceId));
      await dispatch(fetchAllInvoices({ page: 1, limit: 0 }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update invoice terms');
    }
  }
);

export const uploadAdditionalDocuments = createAsyncThunk(
  'invoices/uploadAdditionalDocuments',
  async ({ invoiceId, documents }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.uploadAdditionalDocuments(invoiceId, documents);
      await dispatch(fetchInvoiceDetails(invoiceId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to upload documents');
    }
  }
);

export const createDraftInvoice = createAsyncThunk(
  'invoices/createDraftInvoice',
  async (invoiceData, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.createDraftInvoice(invoiceData);
      await dispatch(fetchUserDraftInvoices());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create draft invoice');
    }
  }
);

export const createAdminDraftInvoice = createAsyncThunk(
  'invoices/createAdminDraftInvoice',
  async (invoiceData, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.createAdminDraftInvoice(invoiceData);
      await dispatch(fetchAdminDraftInvoices({ page: 1, limit: 0 }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create draft invoice');
    }
  }
);

export const fetchUserDraftInvoices = createAsyncThunk(
  'invoices/fetchUserDraftInvoices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await invoicesApi.getUserDraftInvoices();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch draft invoices');
    }
  }
);

export const fetchAdminDraftInvoices = createAsyncThunk(
  'invoices/fetchAdminDraftInvoices',
  async ({ page = 1, limit = 0 }, { rejectWithValue }) => {
    try {
      const response = await invoicesApi.getAdminDraftInvoices(page, limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch draft invoices');
    }
  }
);

export const fetchAllFundedInvoices = createAsyncThunk(
  'invoices/fetchAllFundedInvoices',
  async ({ page = 1, limit = 0, searchTerm = '' }, { rejectWithValue }) => {
    try {
      const response = await invoicesApi.getAllFundedInvoices(page, limit, searchTerm);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch funded invoices');
    }
  }
);

export const attachInvoicePayment = createAsyncThunk(
  'invoices/attachPayment',
  async ({ invoiceId, paymentData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.attachInvoicePayment(invoiceId, paymentData);
      // Refresh invoice details after payment
      await dispatch(fetchAdminInvoiceDetails(invoiceId));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to attach payment'
      );
    }
  }
);

export const updateUserInvoiceStatus = createAsyncThunk(
  'invoices/updateUserInvoiceStatus',
  async ({ invoiceId, status, reason }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.updateUserInvoiceStatus(invoiceId, { status, reason });
      
      // Refresh the invoices list after status update
      await dispatch(fetchUserInvoices({ page: 1, limit: 0 }));
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update invoice status');
    }
  }
);

export const addInvoiceCharges = createAsyncThunk(
  'invoices/addCharges',
  async ({ invoiceId, chargesData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.addInvoiceCharges(invoiceId, chargesData);
      await dispatch(fetchAdminInvoiceDetails(invoiceId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add charges');
    }
  }
);

export const updateInvoicePayment = createAsyncThunk(
  'invoices/updatePayment',
  async ({ invoiceId, paymentId, paymentData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.updateInvoicePayment(paymentId, paymentData);
      // Refresh invoice details after payment update
      await dispatch(fetchAdminInvoiceDetails(invoiceId));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update payment'
      );
    }
  }
);

export const deleteInvoicePayment = createAsyncThunk(
  'invoices/deletePayment',
  async ({ invoiceId, paymentId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.deleteInvoicePayment(paymentId);
      // Refresh invoice details after payment deletion
      await dispatch(fetchAdminInvoiceDetails(invoiceId));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete payment'
      );
    }
  }
);

export const updateInvoiceCharge = createAsyncThunk(
  'invoices/updateCharge',
  async ({ invoiceId, chargeId, chargeData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.updateInvoiceCharge(chargeId, chargeData);
      await dispatch(fetchAdminInvoiceDetails(invoiceId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update charge');
    }
  }
);

export const deleteInvoiceCharge = createAsyncThunk(
  'invoices/deleteCharge',
  async ({ invoiceId, chargeId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await invoicesApi.deleteInvoiceCharge(chargeId);
      await dispatch(fetchAdminInvoiceDetails(invoiceId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete charge');
    }
  }
);

// Add other actions here
